import { create } from "zustand";

interface DialogContentProps {
  title?: string;
  icon?: React.ReactNode;
  description?: string;
  content?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  maxWidth?: string;
  onClose?: () => void;
  preventOutsideClose?: boolean;
  hideCloseButton?: boolean;
}

interface DialogState {
  isOpen: boolean;
  dialogProps: DialogContentProps | null;
  pendingConfirmAction: (() => void) | null;
  actions: {
    openDialog: (dialogProps: DialogContentProps) => void;
    updateDialogProps: (dialogProps: Partial<DialogContentProps>) => void;
    closeDialog: () => void;
    setPendingConfirmAction: (action: (() => void) | null) => void;
    executePendingConfirmAction: () => void;
  };
}

const useDialogStore = create<DialogState>((set, get) => ({
  isOpen: false,
  dialogProps: null,
  pendingConfirmAction: null,
  actions: {
    openDialog: (dialogProps: DialogContentProps) =>
      set({ isOpen: true, dialogProps }),
    updateDialogProps: (dialogProps: Partial<DialogContentProps>) => {
      const currentProps = get().dialogProps;
      if (currentProps) {
        set({ dialogProps: { ...currentProps, ...dialogProps } });
      }
    },
    closeDialog: () => {
      const { dialogProps } = get();
      if (dialogProps?.onClose) {
        dialogProps.onClose();
      }
      set({ isOpen: false, dialogProps: null, pendingConfirmAction: null });
    },
    setPendingConfirmAction: (action) =>
      set({ pendingConfirmAction: action }),
    executePendingConfirmAction: () => {
      const { pendingConfirmAction } = get();
      set({ isOpen: false, dialogProps: null, pendingConfirmAction: null });
      pendingConfirmAction?.();
    },
  },
}));

export const useIsDialogIsOpen = () => useDialogStore(state => state.isOpen);
export const useDialogProps = () => useDialogStore((state) => state.dialogProps);
export const useDialogActions = () => useDialogStore((state) => state.actions);